/**
 * Grid navigation for entities.
 *
 * A single shared A* workspace serves every agent. Requests are queued and
 * expanded against a per-frame node budget, so one long path across a big level
 * costs a few frames of latency instead of a frame spike — the agent keeps
 * following its previous path in the meantime.
 *
 * Cell paths are string-pulled with a thickened line-of-sight test before they
 * are handed back, which is what stops bodies from sawing back and forth at
 * doorway corners.
 *
 * Solid props are not on the grid — a half-wall crossing a room lives between
 * two cell centres, not in either of them — so they are folded in as a separate
 * layer built once per level (`PropField`): a per-cell flag for "scenery here",
 * a hard block for cells nothing can stand in, and a per-cell bitmask of the
 * eight steps scenery closes off. The search reads two array entries per
 * expansion for all of it. Without this a hound walks through the piano it is
 * chasing you around, which destroys the illusion at the exact moment it
 * matters most.
 */

import { TUNING } from '../core/config';
import { hasLineOfSight, NEIGHBORS_8 } from '../core/grid';
import type { PropIndex } from '../core/props';
import { Cell, type Grid, type PropSpot, type Vec2 } from '../core/types';

/** `NEIGHBORS_8` split into flat arrays so the A* inner loop does no tuple work. */
const NX = Int8Array.from(NEIGHBORS_8, (n) => n[0]);
const NY = Int8Array.from(NEIGHBORS_8, (n) => n[1]);
/** Index of the direction opposite each `NEIGHBORS_8` entry. */
const NOPP = Int8Array.from(NEIGHBORS_8, (n) => NEIGHBORS_8.findIndex((m) => m[0] === -n[0] && m[1] === -n[1]));

/** Nodes expanded per frame across all queued queries. */
const NODE_BUDGET_PER_FRAME = 1400;
/** Hard ceiling per query; past this we hand back the best partial route. */
const MAX_EXPANSIONS = 9000;
/** Waypoint capacity of a single path. */
const MAX_WAYPOINTS = 96;
/** Cell capacity of the raw route before string-pulling. */
const MAX_CELLS = 768;

const SQRT2 = Math.SQRT2;
/**
 * Slight over-weighting of the heuristic. Paths stay sane and the search
 * touches far fewer nodes, which matters more here than optimality.
 */
const HEURISTIC_WEIGHT = 1.08;

/** Extra cost for hugging a wall. Keeps bodies out in the middle of corridors. */
const WALL_HUG_COST = 0.35;
/** Extra cost for wading and for squeezing past a pillar. */
const WATER_COST = 0.6;
const PILLAR_COST = 0.9;
/** Feel numbers for routing around scenery. Documented in `core/config.ts`. */
const PROP_COST = TUNING.entities.propCost;
const NAV_RADIUS = TUNING.entities.navRadius;
const PROP_FLAG_HALO = TUNING.entities.propFlagHalo;

/** A followable route in world space. Owned by an agent, filled by the navigator. */
export interface Path {
  /** Waypoints. Only the first `count` entries are meaningful; the array is reused. */
  readonly pts: Vec2[];
  count: number;
  /** Index of the waypoint currently being steered toward. */
  index: number;
  /** True when `pts` describes a usable route. */
  valid: boolean;
  /** True while the navigator still has queued work for this path. */
  pending: boolean;
  /** The goal this route was computed for. */
  readonly goal: Vec2;
}

/** Allocate a path with its waypoint storage up front. */
export function makePath(): Path {
  const pts: Vec2[] = new Array(MAX_WAYPOINTS);
  for (let i = 0; i < MAX_WAYPOINTS; i++) pts[i] = { x: 0, z: 0 };
  return { pts, count: 0, index: 0, valid: false, pending: false, goal: { x: 0, z: 0 } };
}

export function clearPath(path: Path): void {
  path.count = 0;
  path.index = 0;
  path.valid = false;
  path.pending = false;
}

/* ------------------------------------------------------------------ *
 * Solid scenery
 * ------------------------------------------------------------------ */

/**
 * The level's solid props, in the form navigation and steering need them.
 *
 * `PropIndex` answers the exact questions and is shared with the player's
 * collision, so a prop that stops one stops the other. What it cannot do is
 * answer them cheaply enough to sample a two-hundred-metre route: every call is
 * a spatial query. This wraps it in a per-cell flag so the overwhelming
 * majority of a level — which has no scenery within a metre of it — costs one
 * array read.
 */
export interface PropField {
  /** True when a body of `radius` centred here is inside solid scenery. */
  blocked(x: number, z: number, radius: number): boolean;
  /** Push a body out of any scenery it overlaps, in place. True when it moved. */
  eject(pos: Vec2, radius: number): boolean;
  /** True when a straight walk from `a` to `b` clears every prop on the way. */
  clearWalk(a: Vec2, b: Vec2, radius: number): boolean;
  /** True when scenery is close enough to this cell to be worth an exact test. */
  near(cx: number, cy: number): boolean;
  /** Number of solid props on the level. Zero means every query is free. */
  readonly count: number;
}

class Field implements PropField {
  private readonly flags: Uint8Array;
  private readonly w: number;
  private readonly h: number;
  private readonly cs: number;
  readonly count: number;

  constructor(
    grid: Grid,
    props: readonly PropSpot[],
    private readonly index: PropIndex,
  ) {
    this.w = grid.w;
    this.h = grid.h;
    this.cs = grid.cellSize;
    this.flags = new Uint8Array(grid.w * grid.h);
    this.count = index.count;

    for (const p of props) {
      const f = p.footprint;
      if (!f) continue;
      // Matches the cut-off `PropIndex` applies: debris a body walks over is
      // not something to route around.
      if (f.height < TUNING.player.stepOverHeight) continue;
      const reach = Math.hypot(f.halfX, f.halfZ) * (p.scale || 1) + PROP_FLAG_HALO;
      const minX = Math.max(0, Math.floor((p.pos.x - reach) / this.cs));
      const maxX = Math.min(this.w - 1, Math.floor((p.pos.x + reach) / this.cs));
      const minY = Math.max(0, Math.floor((p.pos.z - reach) / this.cs));
      const maxY = Math.min(this.h - 1, Math.floor((p.pos.z + reach) / this.cs));
      for (let cy = minY; cy <= maxY; cy++) {
        for (let cx = minX; cx <= maxX; cx++) this.flags[cy * this.w + cx] = 1;
      }
    }
  }

  near(cx: number, cy: number): boolean {
    if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return false;
    return this.flags[cy * this.w + cx] === 1;
  }

  blocked(x: number, z: number, radius: number): boolean {
    if (this.count === 0) return false;
    return this.index.blocked(x, z, radius);
  }

  eject(pos: Vec2, radius: number): boolean {
    if (this.count === 0) return false;
    return this.index.resolveCircle(pos, radius);
  }

  clearWalk(a: Vec2, b: Vec2, radius: number): boolean {
    if (this.count === 0) return true;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const l = Math.hypot(dx, dz);
    const n = Math.max(1, Math.ceil(l / Math.max(0.2, Math.min(radius, this.cs * 0.4))));
    const spacing = l / n;
    /*
     * Discs that far apart do not quite cover the corridor the body sweeps: a
     * prop grazing the edge of it can fall in the scallop between two samples
     * and be missed, which puts a route through the corner of a piano. Growing
     * each sample by the half-spacing closes the gap exactly — the union of the
     * grown discs then contains the whole corridor — for the same number of
     * tests. It errs toward calling a graze a block, which is the safe way to
     * be wrong.
     */
    const r = Math.hypot(radius, spacing * 0.5);
    // The flag halo only covers bodies up to `PROP_FLAG_HALO`. Anything wider
    // pays for the exact test everywhere rather than get a wrong cheap answer.
    const useFlags = r <= PROP_FLAG_HALO;
    // Sample 1..n, not 0: where the body already stands is not in question, and
    // testing it would make a body that has been shoved into a prop believe it
    // cannot walk anywhere at all.
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const x = a.x + dx * t;
      const z = a.z + dz * t;
      if (useFlags && !this.near(Math.floor(x / this.cs), Math.floor(z / this.cs))) continue;
      if (this.index.blocked(x, z, r)) return false;
    }
    return true;
  }
}

/** Build the per-level scenery lookup. Cheap; it is one pass over the props. */
export function makePropField(grid: Grid, props: readonly PropSpot[], index: PropIndex): PropField {
  return new Field(grid, props, index);
}

/* ------------------------------------------------------------------ *
 * Pathfinding
 * ------------------------------------------------------------------ */

/** The shared pathfinder. One per entity system. */
export interface Navigator {
  /**
   * Point the navigator at a new level and drop all queued work. Without a
   * `field` the search routes on the grid alone, as it did before props became
   * solid.
   */
  setGrid(grid: Grid, field?: PropField | null): void;
  /**
   * Queue a route from `from` to `to`, replacing any queued route for this
   * agent. `radius` is used by the string-pull so the result clears corners.
   */
  request(agentId: number, path: Path, from: Vec2, to: Vec2, radius: number): void;
  /** Drop queued work for an agent, e.g. on despawn. */
  cancel(agentId: number): void;
  /** Spend this frame's node budget. Called once per frame by the entity system. */
  update(): void;
}

interface Query {
  agentId: number;
  path: Path;
  radius: number;
  startIdx: number;
  goalIdx: number;
  /** Exact world goal; the last waypoint snaps to this rather than a cell centre. */
  goal: Vec2;
  from: Vec2;
  started: boolean;
  expanded: number;
  /** Set once this query has given up on routing around the scenery. */
  ignoreProps: boolean;
  /** True when the search ran out of open nodes rather than out of budget. */
  exhausted: boolean;
}

/* Scratch reused by the string-pull so following a path allocates nothing. */
const scratchA: Vec2 = { x: 0, z: 0 };
const scratchB: Vec2 = { x: 0, z: 0 };
const scratchAnchor: Vec2 = { x: 0, z: 0 };
const rawCells = new Int32Array(MAX_CELLS);
const rawPts: Vec2[] = Array.from({ length: MAX_CELLS }, () => ({ x: 0, z: 0 }));

class NavigatorImpl implements Navigator {
  private grid: Grid | null = null;
  private field: PropField | null = null;
  private w = 0;
  private h = 0;

  private g = new Float32Array(0);
  private f = new Float32Array(0);
  private came = new Int32Array(0);
  private stamp = new Int32Array(0);
  private state = new Uint8Array(0);
  private heap = new Int32Array(0);
  private heapPos = new Int32Array(0);
  /** Per-cell movement surcharge, precomputed once per level. */
  private penalty = new Float32Array(0);
  /** 1 where scenery leaves no room for a body to stand at the cell centre. */
  private propBlock = new Uint8Array(0);
  /** Bit `d` set where scenery closes off the step to `NEIGHBORS_8[d]`. */
  private propEdge = new Uint8Array(0);
  private heapSize = 0;
  private gen = 0;

  /** Best node seen this query, used to salvage a partial route on failure. */
  private bestIdx = -1;
  private bestH = Infinity;

  /** Cell centres for the scenery pass, reused so the build allocates nothing. */
  private readonly cellA: Vec2 = { x: 0, z: 0 };
  private readonly cellB: Vec2 = { x: 0, z: 0 };

  private queue: Query[] = [];
  private active: Query | null = null;

  setGrid(grid: Grid, field: PropField | null = null): void {
    this.grid = grid;
    this.field = field && field.count > 0 ? field : null;
    this.queue.length = 0;
    this.active = null;
    if (grid.w !== this.w || grid.h !== this.h) {
      this.w = grid.w;
      this.h = grid.h;
      const n = grid.w * grid.h;
      this.g = new Float32Array(n);
      this.f = new Float32Array(n);
      this.came = new Int32Array(n);
      this.stamp = new Int32Array(n).fill(-1);
      this.state = new Uint8Array(n);
      this.heap = new Int32Array(n);
      this.heapPos = new Int32Array(n);
      this.penalty = new Float32Array(n);
      this.propBlock = new Uint8Array(n);
      this.propEdge = new Uint8Array(n);
    } else {
      this.stamp.fill(-1);
    }
    this.gen = 0;
    this.heapSize = 0;
    this.buildPropData(grid);
    this.buildPenalties(grid);
  }

  /**
   * Wall proximity, water, pillars and scenery are baked into a per-cell
   * surcharge so the inner loop stays a couple of array reads.
   */
  private buildPenalties(grid: Grid): void {
    const { w, h } = grid;
    const field = this.field;
    for (let cy = 0; cy < h; cy++) {
      for (let cx = 0; cx < w; cx++) {
        const i = cy * w + cx;
        if (!grid.isWalkable(cx, cy)) {
          this.penalty[i] = 0;
          continue;
        }
        let p = 0;
        const c = grid.at(cx, cy);
        if (c === Cell.Water) p += WATER_COST;
        else if (c === Cell.Pillar) p += PILLAR_COST;
        if (
          grid.isSolid(cx + 1, cy) ||
          grid.isSolid(cx - 1, cy) ||
          grid.isSolid(cx, cy + 1) ||
          grid.isSolid(cx, cy - 1)
        ) {
          p += WALL_HUG_COST;
        }
        if (field && field.near(cx, cy)) p += PROP_COST;
        this.penalty[i] = p;
      }
    }
  }

  /**
   * Turn the scenery into something the inner loop can read: which cells a body
   * cannot stand in, and which of the eight steps out of a cell it cannot take.
   *
   * Only cells the field has flagged are tested, so the cost is proportional to
   * the number of props rather than to the size of the level, and each edge is
   * tested once and written to both of its cells.
   */
  private buildPropData(grid: Grid): void {
    this.propBlock.fill(0);
    this.propEdge.fill(0);
    const field = this.field;
    if (!field) return;

    const { w, h } = grid;
    const cs = grid.cellSize;
    const a = this.cellA;
    const b = this.cellB;
    for (let cy = 0; cy < h; cy++) {
      for (let cx = 0; cx < w; cx++) {
        if (!field.near(cx, cy) || !grid.isWalkable(cx, cy)) continue;
        const i = cy * w + cx;
        a.x = (cx + 0.5) * cs;
        a.z = (cy + 0.5) * cs;
        // A waypoint is a cell centre, so a centre a body cannot occupy is a
        // waypoint it would walk into a bathtub trying to reach.
        if (field.blocked(a.x, a.z, NAV_RADIUS)) this.propBlock[i] = 1;

        for (let d = 0; d < 8; d++) {
          const ox = NX[d]!;
          const oy = NY[d]!;
          const nx = cx + ox;
          const ny = cy + oy;
          if (!grid.isWalkable(nx, ny)) continue;
          // An undirected edge between two flagged cells is owned by whichever
          // of them the "forward" half of the compass points away from, so it
          // is only tested once. An edge to an unflagged cell has no other
          // owner and is always tested here.
          const forward = oy > 0 || (oy === 0 && ox > 0);
          if (!forward && field.near(nx, ny)) continue;
          b.x = (nx + 0.5) * cs;
          b.z = (ny + 0.5) * cs;
          if (field.clearWalk(a, b, NAV_RADIUS)) continue;
          this.propEdge[i] |= 1 << d;
          this.propEdge[ny * w + nx] |= 1 << NOPP[d]!;
        }
      }
    }
  }

  request(agentId: number, path: Path, from: Vec2, to: Vec2, radius: number): void {
    const grid = this.grid;
    if (!grid) return;

    const start = nearestWalkable(grid, from);
    const goalCell = nearestWalkable(grid, to);
    if (start < 0 || goalCell < 0) {
      path.pending = false;
      path.valid = false;
      return;
    }

    this.cancel(agentId);
    path.pending = true;
    path.goal.x = to.x;
    path.goal.z = to.z;

    this.queue.push({
      agentId,
      path,
      radius,
      startIdx: start,
      goalIdx: goalCell,
      goal: { x: to.x, z: to.z },
      from: { x: from.x, z: from.z },
      started: false,
      expanded: 0,
      ignoreProps: false,
      exhausted: false,
    });
  }

  cancel(agentId: number): void {
    for (let i = this.queue.length - 1; i >= 0; i--) {
      if (this.queue[i]!.agentId === agentId) {
        this.queue[i]!.path.pending = false;
        this.queue.splice(i, 1);
      }
    }
    if (this.active && this.active.agentId === agentId) {
      this.active.path.pending = false;
      this.active = null;
      this.heapSize = 0;
    }
  }

  update(): void {
    if (!this.grid) return;
    let budget = NODE_BUDGET_PER_FRAME;
    while (budget > 0) {
      if (!this.active) {
        const next = this.queue.shift();
        if (!next) return;
        this.active = next;
        this.begin(next);
      }
      budget = this.step(this.active, budget);
    }
  }

  private begin(q: Query): void {
    this.gen++;
    this.heapSize = 0;
    this.bestIdx = -1;
    this.bestH = Infinity;
    q.started = true;
    q.expanded = 0;
    q.exhausted = false;
    this.touch(q.startIdx);
    this.g[q.startIdx] = 0;
    this.f[q.startIdx] = this.heuristic(q.startIdx, q.goalIdx);
    this.push(q.startIdx);
  }

  /** Expand up to `budget` nodes. Returns the unspent budget. */
  private step(q: Query, budget: number): number {
    const grid = this.grid!;
    const w = this.w;

    while (budget > 0) {
      if (this.heapSize === 0) {
        // Nothing left to open: the goal is genuinely unreachable under this
        // query's rules, as opposed to merely too far for the budget.
        q.exhausted = true;
        this.finish(q, this.bestIdx);
        return budget;
      }
      const cur = this.pop();
      if (this.state[cur] === 2) continue;
      this.state[cur] = 2;

      if (cur === q.goalIdx) {
        this.finish(q, cur);
        return budget;
      }

      budget--;
      q.expanded++;
      if (q.expanded > MAX_EXPANSIONS) {
        this.finish(q, this.bestIdx);
        return budget;
      }

      const cx = cur % w;
      const cy = (cur / w) | 0;
      const gCur = this.g[cur]!;
      const edges = this.propEdge[cur]!;

      for (let d = 0; d < 8; d++) {
        const ox = NX[d]!;
        const oy = NY[d]!;
        const nx = cx + ox;
        const ny = cy + oy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= this.h) continue;
        if (!grid.isWalkable(nx, ny)) continue;
        // No corner cutting: a diagonal needs both orthogonal cells open, or
        // bodies clip through the join between two walls.
        if (ox !== 0 && oy !== 0 && (!grid.isWalkable(cx + ox, cy) || !grid.isWalkable(cx, cy + oy))) continue;
        // Scenery in the gap. A half-wall lies between two cell centres and
        // would otherwise be invisible to the search.
        if (!q.ignoreProps && edges & (1 << d)) continue;

        const nIdx = ny * w + nx;
        // Nothing can stand at this cell's centre. The goal cell is exempt,
        // because the tail waypoint is snapped to the real target rather than
        // to the centre: a player standing beside a phone booth can put the
        // centre of their own cell inside it, and refusing the cell would make
        // them unreachable while they stood there.
        if (!q.ignoreProps && this.propBlock[nIdx] === 1 && nIdx !== q.goalIdx) continue;
        this.touch(nIdx);
        if (this.state[nIdx] === 2) continue;

        const stepCost = (ox !== 0 && oy !== 0 ? SQRT2 : 1) + this.penalty[nIdx]!;
        const ng = gCur + stepCost;
        if (ng >= this.g[nIdx]!) continue;

        this.g[nIdx] = ng;
        const hh = this.heuristic(nIdx, q.goalIdx);
        this.f[nIdx] = ng + hh;
        this.came[nIdx] = cur;
        if (hh < this.bestH) {
          this.bestH = hh;
          this.bestIdx = nIdx;
        }
        if (this.state[nIdx] === 1) this.siftUp(this.heapPos[nIdx]!);
        else {
          this.state[nIdx] = 1;
          this.push(nIdx);
        }
      }
    }
    return budget;
  }

  /** Write the route (or a failure) into the requester's path and retire the query. */
  private finish(q: Query, endIdx: number): void {
    const grid = this.grid!;
    const path = q.path;

    /*
     * Scenery may narrow the way through a level; it may never cut one in two.
     * When a search runs out of nodes without reaching the goal, the furniture
     * has sealed a route that the building leaves open — so drop the scenery
     * rules and plan again on the building alone. The body then walks the route
     * and shoulders whatever is on it, which is a monster briefly clipping a
     * chair rather than a monster standing still for the rest of the run.
     */
    if (this.field && !q.ignoreProps && q.exhausted && endIdx !== q.goalIdx) {
      q.ignoreProps = true;
      this.begin(q);
      return;
    }
    path.pending = false;
    this.active = null;
    this.heapSize = 0;

    if (endIdx < 0 || this.stamp[endIdx] !== this.gen) {
      path.valid = false;
      path.count = 0;
      path.index = 0;
      return;
    }

    // Walk the parent chain back to the start, then reverse in place.
    let n = 0;
    let cur = endIdx;
    while (cur >= 0 && n < MAX_CELLS) {
      rawCells[n++] = cur;
      if (cur === q.startIdx) break;
      cur = this.came[cur]!;
      if (this.stamp[cur] !== this.gen) break;
    }
    for (let i = 0, j = n - 1; i < j; i++, j--) {
      const t = rawCells[i]!;
      rawCells[i] = rawCells[j]!;
      rawCells[j] = t;
    }

    const cs = grid.cellSize;
    for (let i = 0; i < n; i++) {
      const idx = rawCells[i]!;
      rawPts[i]!.x = ((idx % this.w) + 0.5) * cs;
      rawPts[i]!.z = (((idx / this.w) | 0) + 0.5) * cs;
    }
    // Snap the tail to the real goal so entities do not stop at a cell centre.
    if (n > 0 && endIdx === q.goalIdx) {
      rawPts[n - 1]!.x = q.goal.x;
      rawPts[n - 1]!.z = q.goal.z;
    }

    // A route planned without the scenery is string-pulled without it too, or
    // every leg would be refused and the body would get the raw cell chain.
    stringPull(grid, q.ignoreProps ? null : this.field, q.from, rawPts, n, q.radius, path);
    path.valid = path.count > 0;
    path.index = 0;
  }

  private heuristic(a: number, b: number): number {
    const ax = a % this.w;
    const ay = (a / this.w) | 0;
    const bx = b % this.w;
    const by = (b / this.w) | 0;
    const dx = Math.abs(ax - bx);
    const dy = Math.abs(ay - by);
    const lo = dx < dy ? dx : dy;
    const hi = dx < dy ? dy : dx;
    return (hi - lo + SQRT2 * lo) * HEURISTIC_WEIGHT;
  }

  /** Lazily reset a cell for the current query generation. */
  private touch(i: number): void {
    if (this.stamp[i] === this.gen) return;
    this.stamp[i] = this.gen;
    this.g[i] = Infinity;
    this.f[i] = Infinity;
    this.came[i] = -1;
    this.state[i] = 0;
    this.heapPos[i] = -1;
  }

  private push(i: number): void {
    const k = this.heapSize++;
    this.heap[k] = i;
    this.heapPos[i] = k;
    this.siftUp(k);
  }

  private pop(): number {
    const top = this.heap[0]!;
    this.heapPos[top] = -1;
    const last = this.heap[--this.heapSize]!;
    if (this.heapSize > 0) {
      this.heap[0] = last;
      this.heapPos[last] = 0;
      this.siftDown(0);
    }
    return top;
  }

  private siftUp(k: number): void {
    const i = this.heap[k]!;
    const fi = this.f[i]!;
    while (k > 0) {
      const p = (k - 1) >> 1;
      const j = this.heap[p]!;
      if (this.f[j]! <= fi) break;
      this.heap[k] = j;
      this.heapPos[j] = k;
      k = p;
    }
    this.heap[k] = i;
    this.heapPos[i] = k;
  }

  private siftDown(k: number): void {
    const i = this.heap[k]!;
    const fi = this.f[i]!;
    const n = this.heapSize;
    for (;;) {
      let c = k * 2 + 1;
      if (c >= n) break;
      const r = c + 1;
      if (r < n && this.f[this.heap[r]!]! < this.f[this.heap[c]!]!) c = r;
      const j = this.heap[c]!;
      if (this.f[j]! >= fi) break;
      this.heap[k] = j;
      this.heapPos[j] = k;
      k = c;
    }
    this.heap[k] = i;
    this.heapPos[i] = k;
  }
}

export function makeNavigator(): Navigator {
  return new NavigatorImpl();
}

/* ------------------------------------------------------------------ *
 * Geometry helpers shared by the navigator and the steering code
 * ------------------------------------------------------------------ */

/**
 * Line of sight for a body of a given radius: the centre line plus two rails
 * offset perpendicular to it. Three Bresenham walks is cheap and is the
 * difference between a smooth corner and a body wedged in a doorway.
 *
 * With a `field` this is a walkability test rather than a sight test — scenery
 * blocks the walk. Sight is a separate question and still goes through
 * `hasLineOfSight`, because a hound can plainly see over a half-wall it cannot
 * walk through.
 */
export function clearLine(grid: Grid, a: Vec2, b: Vec2, radius: number, field: PropField | null = null): boolean {
  if (!hasLineOfSight(grid, a, b)) return false;
  if (field && !field.clearWalk(a, b, radius)) return false;
  if (radius <= 0.01) return true;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const l = Math.hypot(dx, dz);
  if (l < 1e-4) return true;
  const px = (-dz / l) * radius;
  const pz = (dx / l) * radius;

  scratchA.x = a.x + px;
  scratchA.z = a.z + pz;
  scratchB.x = b.x + px;
  scratchB.z = b.z + pz;
  if (!hasLineOfSight(grid, scratchA, scratchB)) return false;

  scratchA.x = a.x - px;
  scratchA.z = a.z - pz;
  scratchB.x = b.x - px;
  scratchB.z = b.z - pz;
  return hasLineOfSight(grid, scratchA, scratchB);
}

/**
 * Collapse a cell-by-cell route into the fewest waypoints a body of `radius`
 * can walk in straight lines. Writes into `path`.
 */
function stringPull(
  grid: Grid,
  field: PropField | null,
  from: Vec2,
  pts: Vec2[],
  n: number,
  radius: number,
  path: Path,
): void {
  path.count = 0;
  if (n === 0) return;

  scratchAnchor.x = from.x;
  scratchAnchor.z = from.z;

  // Index 0 is the cell the body already stands in, so the sweep starts past
  // it. Each pass takes the furthest point still visible from the anchor, which
  // costs one line test per cell overall rather than one per pair.
  let i = 1;
  while (i < n && path.count < MAX_WAYPOINTS) {
    let j = i;
    while (j + 1 < n && clearLine(grid, scratchAnchor, pts[j + 1]!, radius, field)) j++;
    const wp = path.pts[path.count++]!;
    wp.x = pts[j]!.x;
    wp.z = pts[j]!.z;
    scratchAnchor.x = wp.x;
    scratchAnchor.z = wp.z;
    i = j + 1;
  }
}

/** Flat index of the nearest walkable cell to a world point, or -1. */
export function nearestWalkable(grid: Grid, pos: Vec2): number {
  const c = grid.worldToCell(pos.x, pos.z);
  if (grid.isWalkable(c.cx, c.cy)) return c.cy * grid.w + c.cx;
  // Spiral out a little; entities only end up here after being shoved.
  for (let r = 1; r <= 3; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const nx = c.cx + dx;
        const ny = c.cy + dy;
        if (grid.isWalkable(nx, ny)) return ny * grid.w + nx;
      }
    }
  }
  return -1;
}

/**
 * Half-width of the square pillar in a `Cell.Pillar`, as a fraction of the cell.
 * The same number the player's collision and the pillar mesh use — an entity
 * that stops 19 cm short of a column it can see is not navigating the level the
 * player is standing in.
 */
const PILLAR_HALF = TUNING.player.pillarHalfFraction;

/**
 * True when a body of `radius` centred here would overlap something solid.
 * With a `field`, solid scenery counts as solid too.
 */
export function blocked(grid: Grid, x: number, z: number, radius: number, field: PropField | null = null): boolean {
  if (
    grid.isSolidAt(x - radius, z - radius) ||
    grid.isSolidAt(x + radius, z - radius) ||
    grid.isSolidAt(x - radius, z + radius) ||
    grid.isSolidAt(x + radius, z + radius)
  ) {
    return true;
  }
  // Pillars are walkable cells with a solid core; treat the core as a wall.
  const cs = grid.cellSize;
  const cx = Math.floor(x / cs);
  const cy = Math.floor(z / cs);
  if (grid.at(cx, cy) === Cell.Pillar) {
    const half = cs * PILLAR_HALF + radius;
    if (Math.abs(x - (cx + 0.5) * cs) < half && Math.abs(z - (cy + 0.5) * cs) < half) return true;
  }
  if (field && field.blocked(x, z, radius)) return true;
  return false;
}

/**
 * Move a body, sliding along whatever it hits. Axis-separated so a body pressed
 * into a corner keeps its remaining freedom instead of stopping dead.
 *
 * Passing a `field` makes scenery solid to the body. A body that is *already*
 * inside a prop — shoved there by another entity, or spawned into one — is
 * moved on the grid alone and then pushed out, because a body that refuses
 * every step while overlapping something would stay in it forever.
 */
export function slideMove(
  grid: Grid,
  pos: Vec2,
  dx: number,
  dz: number,
  radius: number,
  field: PropField | null = null,
): void {
  const embedded = field ? field.blocked(pos.x, pos.z, radius) : false;
  const solid = embedded ? null : field;

  const step = Math.hypot(dx, dz);
  // Long steps (a chasing hound at 60fps is fine, but a hitch is not) are split
  // so nothing tunnels through a wall — or through a partition, which is
  // thinner than a wall and the reason the substep is measured in radii.
  const sub = step > radius ? Math.min(4, Math.ceil(step / radius)) : 1;
  const sx = dx / sub;
  const sz = dz / sub;
  for (let i = 0; i < sub; i++) {
    const nx = pos.x + sx;
    if (!blocked(grid, nx, pos.z, radius, solid)) pos.x = nx;
    const nz = pos.z + sz;
    if (!blocked(grid, pos.x, nz, radius, solid)) pos.z = nz;
  }

  if (!field) return;
  const px = pos.x;
  const pz = pos.z;
  // The index leaves its own skin, so the body comes out just clear of the
  // surface rather than exactly on it — which would read as still embedded.
  if (!field.eject(pos, radius)) return;
  // One axis at a time, so that leaving a prop cannot post the body into the
  // wall the prop was standing against.
  if (blocked(grid, pos.x, pz, radius)) pos.x = px;
  if (blocked(grid, pos.x, pos.z, radius)) pos.z = pz;
}

/** Walkable orthogonal + diagonal neighbours of the cell under a world point. */
export function openness(grid: Grid, pos: Vec2, span = 2): number {
  const c = grid.worldToCell(pos.x, pos.z);
  let open = 0;
  for (let dy = -span; dy <= span; dy++) {
    for (let dx = -span; dx <= span; dx++) {
      if (grid.isWalkable(c.cx + dx, c.cy + dy)) open++;
    }
  }
  return open;
}

/** How many of the four orthogonal neighbours are walkable. Low means corridor. */
export function corridorScore(grid: Grid, cx: number, cy: number): number {
  let n = 0;
  if (grid.isWalkable(cx + 1, cy)) n++;
  if (grid.isWalkable(cx - 1, cy)) n++;
  if (grid.isWalkable(cx, cy + 1)) n++;
  if (grid.isWalkable(cx, cy - 1)) n++;
  return n;
}
